<template>
  <ScrollArea>
    <div
      v-if="accessDenied2" >
      <h1 class="settings__title">
        Organisation contact fields
      </h1>
      <Forbidden />
    </div>

    <div
      v-else-if="!forbidden"
      class="settings--limit-width">
      <h1 class="settings__title">
        Organisation contact fields
      </h1>

      <InfoCard
        v-if="!errorMessage && !loadingCustomFields && !customFieldsExist"
        :flat-icon="SBIconList.CONTACT_FIELD"
        title="Add contact fields"
        description="Use contact fields to add more details to your contacts that may be useful for your business workflows. You can click on any contact field below contact number details, inside the conversation, to add details."
        action-text="Add contact fields"
        @action="openAddContactFieldModal" />

      <Spinner
        v-else-if="loadingCustomFields"
        class="mt-5"
        :thickness="2" />

      <SettingsNote
        v-else-if="errorMessage"
        :icon="SBIconList.ERROR"
        text="Error loading data..." />

      <template v-else>
        <!-- ACTION BAR -->
        <div
          class="sticky-top" >
          <!-- Search section -->
          <div class="flex space-between settings--box bottom-20">
            <SearchInput
              ref="searchInput"
              v-model="searchQuery"
              :no-debounce="true"
              placeholder="Search contact fields..." />

            <b-button
              class="flex center-all"
              @click="openAddContactFieldModal">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.PLUS"
                :color="SBColors.blackPrimary"
                :size="8"/>
              <span>New</span>
            </b-button>
          </div>
        </div>

        <Spinner
          v-if="loadingCustomFieldsFaked"
          style="height: 65px;"
          class="mt-5 spinner-border-top"
          :thickness="2" />

        <!-- No data info -->
        <div
          v-else-if="!filteredCustomFieldList.length"
          class="no-data flex center-all">
          <span class="wrapper flex center-all">
            <span class="emoji">😐</span>
            <p>We couldn’t find anything for <strong>{{ searchQuery }}</strong></p>
          </span>
        </div>

        <!-- TABLE -->
        <div
          v-else
          ref="tableGrid"
          class="table__grid clickable bottom-40">
          <span class="column-name sticky-top sticky-second">Name</span>
          <span class="column-name sticky-top sticky-second">Type</span>
          <span class="column-name sticky-top sticky-second">Created at</span>
          <span class="column-name sticky-top sticky-second" />

          <span
            v-if="true"
            class="full-width table-count">CUSTOM FIELDS ({{ filteredCustomFieldList.length }})</span>

          <template
            v-for="(tag, index) in filteredCustomFieldList" >
            <!-- Name column cell -->
            <div
              :key="`tag-${index}`"
              class="grid-item"
              :class="[`hover-${index}`, 'flex', 'flex-align-center', {hide: !filteredCustomFieldListIds.includes(tag.id)}]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(tag)" >
              <p
                class="ellipsis"
                :inner-html.prop="(!tag.name ? 'No name' : tag.name) | boldMatch(searchQuery)" />
            </div>

  <!-- TODO fix this to show yes/no instead boolean -->
            <!-- Type column cell -->
            <div
              :key="`type-${index}`"
              class="grid-item"
              :class="[`hover-${index} flex flex-align-center`, {hide: !filteredCustomFieldListIds.includes(tag.id)}]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(tag)" >
              <p class="ellipsis first-uppercase">{{ fieldType(tag) }}</p>
            </div>

            <!-- Created at column cell -->
            <div
              :key="`join-${index}`"
              class="grid-item"
              :class="[`hover-${index} flex flex-align-center`, {hide: !filteredCustomFieldListIds.includes(tag.id)}]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(tag)" >
              <p
                v-if="tag.created_at"
                class="ellipsis">{{ tag.created_at | tableDate }}</p>
              <p
                v-else
                class="ellipsis no-table-data">No date</p>
            </div>

            <!-- Menu column cell -->
            <div
              :key="`action-${index}`"
              class="grid-item"
              :class="[`hover-${index} flex flex-align-center`, {hide: !filteredCustomFieldListIds.includes(tag.id)}]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(tag)" >
              <b-dropdown
                no-caret
                right
                toggle-class="settings__table-action"
                @click.stop.prevent
                @show="menuOpen[index] = true"
                @hide="controlHide($event, index)">
                <template #button-content>
                  <span
                    class="menu-button flex center-all"
                    :class="{active: menuOpen[index]}" />
                </template>
                <b-dropdown-item @click.stop.prevent="editContactField(tag)" >Edit</b-dropdown-item>
                <b-dropdown-item @click.stop.prevent="deleteContactField(tag)" >Delete</b-dropdown-item>
              </b-dropdown>
            </div>
          </template>
        </div>
      </template>

      <CreateContactFieldModal ref="createContactFieldModal" />

    </div>
  </ScrollArea>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import escapeStringRegexp from 'escape-string-regexp';

import ToasterMixin from '@/mixins/Toaster';
import Spinner from '../../common/Spinner';
import ScrollArea from '@/components/common/ScrollArea';
import NoPermissionMixin from '@/mixins/AccessPermission';
import InfoCard from '@/components/common/InfoCard';
import SettingsNote from './partials/SettingsNote';
import SearchInput from '@/components/business/widgets/SearchInput';
import CreateContactFieldModal from '@/components/business/settings/modals/CreateContactField.modal';
import fieldTypes from '@/helpers/lists/fieldTypes';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';

export default {
  name: 'ContactFieldsSettings',
  components: {
    Spinner,
    ScrollArea,
    InfoCard,
    SettingsNote,
    SearchInput,
    CreateContactFieldModal,
  },
  mixins: [
    ToasterMixin,
    NoPermissionMixin,
    UserPermissionsMixin,
  ],
  props: {},
  data() {
    return {
      errorMessage: '',
      forbidden: false,
      searchQuery: '',
      menuOpen: [],
      fieldTypes,
      loadingCustomFieldsFaked: false, // Contact fields are not paginated so we will fake the loader for now
    };
  },
  computed: {
    ...mapGetters({
      loadingCustomFields: 'contact/loadingCustomFields',
      customFieldList: 'contact/customFieldList',
      apiError: 'contact/apiError',
    }),
    accessDenied2() {
      return this.forbidden || !this.checkPermission('manage_organisation_settings');
    },
    organisationId() {
      return this.$route.params.organisationId;
    },
    filteredCustomFieldList() {
      return this.customFieldList[this.organisationId].filter(customField => (RegExp(`(${escapeStringRegexp(this.searchQuery)})`, 'i').test(customField.name)));
    },
    filteredCustomFieldListIds() {
      return this.filteredCustomFieldList.map(customField => customField.id);
    },
    customFieldsExist() {
      return this.customFieldList && this.customFieldList[this.organisationId] && this.customFieldList[this.organisationId].length;
    },
  },
  watch: {
    apiError(error) {
      this.errorMessage = error.data.message;
    },
    searchQuery() {
      this.loadingCustomFieldsFaked = true;
      setTimeout(() => {
        this.loadingCustomFieldsFaked = false;
      }, 300);
    },
    // accessDenied() {
    //   if (!this.accessDenied) {
    //     this.fetchCustomFields({
    //       organisationId: this.organisationId,
    //     });
    //   }
    // },
  },
  mounted() {
    // if (!this.accessDenied && this.currentOrganisation.id) {
    //   // in case user returns to the page. On first mount business is undefined so this will not trigger
    //   this.fetchCustomFields({
    //     organisationId: this.organisationId,
    //   });
    // }
    window.eventBus.$on('403', () => { this.forbidden = true; });
    this.finalizeSplashView();
  },
  destroyed() {
    this.reset();
  },
  methods: {
    ...mapActions({
      finalizeSplashView: 'organisation/finalizeSplashView',
      // fetchCustomFields: 'contact/fetchCustomFields20',
      deleteCustomField: 'contact/deleteCustomField',
    }),
    fieldType(field) {
      return this.fieldTypes.find(fieldType => fieldType.alias === field.type).name;
    },
    openAddContactFieldModal() {
      this.$refs.createContactFieldModal.show();
    },
    controlHide(event, index) {
      if (this.noHide) {
        this.noHide = false;
        event.preventDefault();
      } else {
        this.menuOpen[index] = false;
      }
    },
    removeHover(index) {
      const cells = this.$refs.tableGrid.getElementsByClassName(`hover-${index}`);

      for (let index = 0; index < cells.length; index++) {
        const cell = cells[index];
        cell.classList.remove('hover-grid');
        cell.classList.remove('last');
      }
    },
    addHover(index) {
      const cells = this.$refs.tableGrid.getElementsByClassName(`hover-${index}`);

      for (let index = 0; index < cells.length; index++) {
        const cell = cells[index];
        if (index === 0) {
          cell.classList.add('first');
        }
        cell.classList.add('hover-grid');
        if (index === cells.length - 1) {
          cell.classList.add('last');
        }
      }
    },
    deleteContactField(contactField) {
      this.$confirmPluginModal.show({
        buttonVariant: 'RED',
        title: 'Delete contact field',
        message: `Are you sure you want to delete contact field <b>${contactField.name}</b>?`,
        confirmButtonText: 'Delete',
        showUndoWarning: false,
        autoClose: 'NO',
        confirmCallback: () => {
          this.deleteCustomField({
            contactFieldId: contactField.id,
          }).then((response) => {
            if (response.status === 200) {
              this.showToaster('Contact field has been successfully deleted.', null, null, 'OK');
              this.$confirmPluginModal.close();
            }
          });
        },
      });
    },
    editContactField(contactField) {
      this.$refs.createContactFieldModal.show(contactField);
    },
    selectRow(contactField) {
      this.$refs.createContactFieldModal.show(contactField);
    },
    reset() {
      this.errorMessage = '';
      this.searchQuery = '';
      this.menuOpen = [];
    },
  },
};
</script>

<style lang="scss" scoped>
.spinner-border-top {
  border-top: 1px solid #e6ebed;
  margin-top: 0 !important;
  padding-top: 40px;
}

.table__grid {
  grid-template-columns: minmax(50px, 200px) minmax(50px, auto) minmax(0, max-content) min-content !important;

  .full-width {
    grid-column: 1 / 5 !important;
  }

  .no-table-data {
    color: $greyPrimary;
  }
}
</style>
